/**
 * Тост «пока вас не было, пришли уведомления».
 *
 * Колокольчик со счётчиком легко не заметить, вернувшись к задачнику,
 * поэтому о накопившемся сообщаем один раз — тостом. Поводов два:
 *
 * - страницу открыли заново, и сессия восстановилась по cookie;
 * - связь с сервером оборвалась (сон ноутбука, смена сети) и вернулась.
 *
 * «Пока вас не было» — уведомления новее самого свежего, что человек уже
 * застал в этом браузере. Отметка хранится в localStorage отдельно для
 * каждого пользователя и сдвигается, пока задачник открыт: пришедшее при
 * живом сокете видно на колокольчике, и тост о нём был бы лишним.
 */

import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

import { useAppSelector } from "@/app/hooks";
import { toast } from "@/shared/ui";

import type { NotificationItem, NotificationPage } from "@taskflow/client-core/notifications";

const STORAGE_PREFIX = "tf.notifications.seenUntil.";

/** После восстановления связи сервер докатывает пропущенные события не
 *  мгновенно — ждём их столько, прежде чем решить, что нового нет. */
export const RECONNECT_WINDOW_MS = 10_000;

function readMark(key: string): number | null {
  try {
    const raw = localStorage.getItem(key);
    const value = raw === null ? Number.NaN : Number(raw);
    return Number.isFinite(value) ? value : null;
  } catch {
    return null;
  }
}

function writeMark(key: string, value: number): void {
  try {
    localStorage.setItem(key, String(value));
  } catch {
    // Без хранилища тост просто не покажется — не повод ломать колокольчик.
  }
}

function newestTime(items: readonly NotificationItem[]): number | null {
  const times = items.map((item) => Date.parse(item.createdAt)).filter(Number.isFinite);
  return times.length > 0 ? Math.max(...times) : null;
}

export function useMissedNotificationsToast(
  page: NotificationPage | undefined,
  fetchedAt: number | undefined,
  refetch: () => void,
): void {
  const navigate = useNavigate();
  const userId = useAppSelector((state) => state.auth.user?.id);
  const connection = useAppSelector((state) => state.ui.connection);

  // Срок, до которого ждём пропущенное. С монтирования — до первого ответа
  // (страница открыта заново), после восстановления связи — окно.
  const waitUntil = useRef<number | null>(Number.POSITIVE_INFINITY);
  const broken = useRef(false);

  useEffect(() => {
    if (connection === "reconnecting" || connection === "closed") {
      broken.current = true;
    } else if (connection === "open" && broken.current) {
      broken.current = false;
      waitUntil.current = Date.now() + RECONNECT_WINDOW_MS;
      refetch();
    }
  }, [connection, refetch]);

  useEffect(() => {
    if (page === undefined || fetchedAt === undefined || userId === undefined) return;

    const key = STORAGE_PREFIX + userId;
    const seen = readMark(key);
    const deadline = waitUntil.current;

    if (deadline !== null) {
      // Первый вход в этом браузере: «пока вас не было» не с чем сравнить.
      const fresh =
        seen === null || Date.now() > deadline
          ? []
          : page.data.filter((item) => !item.read && Date.parse(item.createdAt) > seen);

      if (fresh.length > 0) {
        waitUntil.current = null;
        // Вся страница новая, а за ней есть ещё — точного числа не знаем.
        const capped = fresh.length === page.data.length && page.pageInfo.hasMore;
        toast.notice(
          "Новые уведомления",
          `Пока вас не было, пришло новых: ${fresh.length}${capped ? "+" : ""}`,
          "Открыть",
          () => void navigate("/notifications"),
        );
      } else if (deadline === Number.POSITIVE_INFINITY || Date.now() > deadline) {
        waitUntil.current = null;
      }
    }

    const newest = newestTime(page.data);
    if (newest !== null && (seen === null || newest > seen)) writeMark(key, newest);
  }, [page, fetchedAt, userId, navigate]);
}
